<template>
  <div class="detail-card" data-card-id="vendor-ai-audit">
    <div
      class="card-header"
      :class="{ 'slidein-header': mode === 'draggable' }"
    >
      <h5 class="card-title">
        Vendor AI Detections Audit <span class="subtitle">(per image)</span>
      </h5>
      <div v-if="mode === 'draggable'" class="header-right">
        <button
          class="drag-toggle-btn"
          @click.prevent="$emit('toggleDraggable')"
          title="Close"
        >
          <font-awesome-icon :icon="['fas', 'times']" />
        </button>
      </div>
    </div>

    <div class="card-content table-responsive">
      <table
        class="table table-borderless table-sm color-white mb-0"
        width="100%"
      >
        <thead>
          <tr class="border-bottom border-blue">
            <th class="ps-0 font-size-18">Detections</th>
            <th class="text-end ps-0" v-if="project?.extra?.display_confidence">
              Confidence<br /><br />
            </th>
            <th
              class="text-center ps-0"
              style="width: 8rem"
              v-if="project?.extra?.location_based"
            >
              Show / Hide<br />
              <a href="#" @click.prevent="toggleAll(true)">
                <font-awesome-icon
                  :icon="['fas', 'eye']"
                  class="icon font-yellow me-1"
                />
              </a>
              /
              <a href="#" @click.prevent="toggleAll(false)">
                <font-awesome-icon
                  :icon="['fas', 'eye-slash']"
                  class="icon color-white ms-1"
                />
              </a>
            </th>
            <th class="text-center ps-0" style="width: 8rem">
              Concordance<br />
              <a href="#" @click.prevent="toggleConcordanceAll(true)">
                <font-awesome-icon
                  :icon="['fas', 'check-square']"
                  class="icon font-yellow me-1"
                />
              </a>
              /
              <a href="#" @click.prevent="toggleConcordanceAll(false)">
                <font-awesome-icon
                  :icon="['fas', 'square']"
                  class="icon color-white ms-1"
                />
              </a>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="pathology in filteredList" :key="pathology._id">
            <td>
              <font-awesome-icon
                :icon="['fas', 'check-circle']"
                class="me-4 font-size-16"
                :style="{ color: pathology.colour }"
              />
              <span>{{
                formatPathologyName(pathology.predicted_diagnosis)
              }}</span>
            </td>
            <td
              class="text-end"
              v-if="
                project?.extra?.display_confidence && pathology.confidence >= 0
              "
            >
              <span class="color-white"
                >{{
                  (Math.round(pathology.confidence * 10000) / 100).toFixed(2)
                }}%</span
              >
              <font-awesome-icon
                :icon="['fas', 'microchip']"
                class="color-yellow ms-2"
              />
            </td>
            <td class="text-end" v-else-if="project?.extra?.display_confidence">
              <font-awesome-icon
                :icon="['fas', 'plus']"
                class="color-blue ms-2"
              />
            </td>
            <td class="text-center" v-if="project?.extra?.location_based">
              <a
                href="#"
                @click.prevent="toggleShown(pathology._id)"
                v-if="
                  isConcordance(pathology._id) !== false &&
                  !pathology.isDiagnosis
                "
              >
                <font-awesome-icon
                  :icon="['fas', 'eye']"
                  class="icon font-yellow ms-2"
                  v-if="isShown(pathology._id) !== false"
                />
                <font-awesome-icon
                  :icon="['fas', 'eye-slash']"
                  class="icon color-white ms-2"
                  v-else
                />
              </a>
            </td>
            <td class="text-center" v-if="pathology.confidence >= 0">
              <a href="#" @click.prevent="toggleConcordance(pathology._id)">
                <font-awesome-icon
                  :icon="['fas', 'square']"
                  class="icon color-white ms-2"
                  v-if="isConcordance(pathology._id) === false"
                />
                <font-awesome-icon
                  :icon="['fas', 'check-square']"
                  class="icon font-yellow ms-2"
                  v-else
                />
              </a>
            </td>
            <td class="text-center" v-else>
              <!-- Delete other pathology logic (omitted as drawing new ones isn't fully ported yet, but keeping placeholder) -->
              <a href="#" @click.prevent="$emit('deleteOther', pathology._id)">
                <font-awesome-icon
                  :icon="['fas', 'trash']"
                  class="color-white ms-2 font-size-14"
                />
              </a>
            </td>
          </tr>
          <tr v-if="availableOptions && availableOptions.length > 0">
            <td colspan="5">
              <span class="font-size-16">Other findings</span><br />
              <b-form-tags
                id="tags-component-select"
                :model-value="otherTags"
                @update:model-value="$emit('update:otherTags', $event)"
                class="mt-2 multi-select"
                no-outer-focus
              >
                <template
                  v-slot="{
                    tags,
                    inputAttrs,
                    inputHandlers,
                    disabled,
                    removeTag,
                    addTag,
                  }"
                >
                  <ul
                    v-if="tags.length > 0"
                    class="list-inline d-inline-block mb-2"
                  >
                    <li v-for="tag in tags" :key="tag" class="list-inline-item">
                      <b-form-tag
                        @remove="removeTag(tag)"
                        :title="tag"
                        :disabled="disabled"
                        >{{ tag }}</b-form-tag
                      >
                    </li>
                  </ul>
                  <b-form-select
                    v-bind="inputAttrs"
                    :disabled="disabled || filteredOptions.length === 0"
                    :options="filteredOptions"
                    size="sm"
                    :model-value="selectedOption"
                    @update:model-value="(val) => handleTagSelect(val, addTag)"
                  >
                    <template #first>
                      <option disabled value="">
                        Please select other findings present in this image
                      </option>
                    </template>
                  </b-form-select>
                </template>
              </b-form-tags>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="card-footer px-3 pb-2 pt-0">
      <div class="row mb-2">
        <div class="col-md-6 offset-md-6">
          <input
            :disabled="savingData"
            type="button"
            @click.prevent="$emit('save')"
            class="btn btn-blue btn-send float-end btn-sm rounded-pill px-3 me-2"
            :value="savingData ? 'Saving Analysis' : 'Save Analysis'"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, nextTick } from "vue";

const props = defineProps({
  aiPathologies: {
    type: Array,
    default: () => [],
  },
  project: {
    type: Object,
    default: null,
  },
  mode: {
    type: String,
    default: "draggable",
  },
  shownList: {
    type: Object,
    default: () => ({}),
  },
  concordanceList: {
    type: Object,
    default: () => ({}),
  },
  colours: {
    type: Array,
    default: () => [],
  },
  availableOptions: {
    type: Array,
    default: () => [],
  },
  otherTags: {
    type: Array,
    default: () => [],
  },
  savingData: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  "toggleDraggable",
  "update:shownList",
  "update:concordanceList",
  "deleteOther",
  "update:otherTags",
  "save",
]);

// Helper for formatting name
const formatPathologyName = (name) => {
  if (!name) return "";
  return name.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
};

const filteredList = computed(() => {
  if (!props.aiPathologies) return [];
  // Filter out cardio/thoracic as per original component logic
  return props.aiPathologies
    .filter(
      (p) =>
        !["thoracic", "cardio"].includes(p.ai.predicted_diagnosis.toLowerCase())
    )
    .map((p) => {
      return {
        ...p.ai,
        colour: p.ai.colour, // Colour is already assigned in parent prop
        _id: p.ai._id,
      };
    });
});

const isShown = (id) => {
  return props.shownList[id] !== false; // Default true
};

const isConcordance = (id) => {
  return props.concordanceList[id] !== false; // Default true
};

const toggleShown = (id) => {
  const newList = { ...props.shownList };
  newList[id] = !isShown(id);
  emit("update:shownList", newList);
};

const toggleConcordance = (id) => {
  const newConcordanceList = { ...props.concordanceList };
  const newShownList = { ...props.shownList };
  const newState = !isConcordance(id);

  newConcordanceList[id] = newState;
  // Sync visibility: if ticked (true) -> show, if unticked (false) -> hide
  newShownList[id] = newState;

  emit("update:concordanceList", newConcordanceList);
  emit("update:shownList", newShownList);
};

const toggleAll = (state) => {
  const newList = { ...props.shownList };
  filteredList.value.forEach((p) => {
    newList[p._id] = state;
  });
  emit("update:shownList", newList);
};

const toggleConcordanceAll = (state) => {
  const newList = { ...props.concordanceList };
  filteredList.value.forEach((p) => {
    newList[p._id] = state;
  });
  emit("update:concordanceList", newList);
};

const selectedOption = ref("");

const filteredOptions = computed(() => {
  return props.availableOptions.filter((opt) => !props.otherTags.includes(opt));
});

const handleTagSelect = (val, addTag) => {
  // Handle both direct value (bootstrap-vue-next) and event object
  const value = val && val.target ? val.target.value : val;

  if (value) {
    addTag(value);
    selectedOption.value = "";
  }
};
</script>

<style scoped>
.table {
  color: white;
}
.icon {
  cursor: pointer;
}
.color-white {
  color: white !important;
}
.color-yellow {
  color: #ffc107 !important;
}
.color-blue {
  color: #0dcaf0 !important;
}
.font-yellow {
  color: #ffc107 !important;
}
.table-borderless th,
.table-borderless td {
  border: none;
  padding: 0.5rem;
  vertical-align: middle;
}
.border-bottom {
  border-bottom: 1px solid #4a5568 !important;
}
.font-size-18 {
  font-size: 1.125rem;
}
.font-size-16 {
  font-size: 1rem;
}
.font-size-14 {
  font-size: 0.875rem;
}
.ms-2 {
  margin-left: 0.5rem;
}
.me-2 {
  margin-right: 0.5rem;
}
.me-4 {
  margin-right: 1.5rem;
}
.ps-0 {
  padding-left: 0;
}
.text-end {
  text-align: right;
}
.text-center {
  text-align: center;
}
</style>
